// Typed WS wrapper for the admin app. Same wire protocol as the game
// client (every payload is a JSON-encoded `ClientMessage` / `ServerMessage`
// from @vinyl/shared), but no auto-reconnect drama — if the connection
// drops, the user just refreshes.

import type { ClientMessage, ServerMessage } from "@vinyl/shared";

type Listener = (msg: ServerMessage) => void;

export class AdminClient {
  private ws: WebSocket | null = null;
  private readonly listeners = new Set<Listener>();
  private connectedListeners: ((open: boolean) => void)[] = [];

  constructor(private readonly url: string) {}

  connect(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      for (const l of this.connectedListeners) l(true);
    };
    ws.onclose = () => {
      for (const l of this.connectedListeners) l(false);
    };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(String(event.data)) as ServerMessage;
        for (const l of this.listeners) l(msg);
      } catch (err) {
        console.error("[admin/net] bad server message:", err);
      }
    };
    ws.onerror = () => {
      // close follows
    };
  }

  send(msg: ClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  onMessage(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onConnection(fn: (open: boolean) => void): () => void {
    this.connectedListeners.push(fn);
    return () => {
      this.connectedListeners = this.connectedListeners.filter((x) => x !== fn);
    };
  }
}
